import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  BookOpen,
  CheckCircle,
  Clock,
  Star,
  Filter,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";

interface ModuleProgress {
  id: string;
  nom: string;
  progression: number;
  evaluation: number;
  dateCompletion: string;
  evaluations: {
    note: number;
    commentaire: string;
    date: string;
  }[];
}

interface Filleul {
  id: string;
  nom: string;
  prenom: string;
  email: string;
  photo: string | null;
  statut: "Actif" | "Inactif";
  derniereConnexion: string;
  progression: ModuleProgress[];
}

type ModuleFilter = "all" | "in-progress" | "completed";

const CollaborateurDetail = () => {
  const { filleulId } = useParams();
  const navigate = useNavigate();
  const [filleul, setFilleul] = useState<Filleul | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [moduleFilter, setModuleFilter] = useState<ModuleFilter>("all");
  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const [totalCollaborateurs, setTotalCollaborateurs] = useState<number>(0);

  useEffect(() => {
    const fetchCollaborateurDetails = async () => {
      try {
        const token = localStorage.getItem("accessToken");
        if (!token) {
          throw new Error("Token d'authentification manquant");
        }

        // Pour le développement, on utilise les données mockées
        const mockCollaborateurs = [
          {
            id: "1",
            nom: "Dupont",
            prenom: "Jean",
            email: "jean.dupont@example.com",
            photo: "/images/jean-kouassi.jpg",
            statut: "Actif" as const,
            derniereConnexion: "2024-03-15T10:30:00",
            progression: [
              {
                id: "1",
                nom: "Module 1",
                progression: 100,
                evaluation: 85,
                dateCompletion: "2024-03-10",
                evaluations: [
                  {
                    note: 80,
                    commentaire: "Bonne compréhension des concepts de base",
                    date: "2024-03-05",
                  },
                  {
                    note: 85,
                    commentaire: "Excellente application des connaissances",
                    date: "2024-03-07",
                  },
                  {
                    note: 90,
                    commentaire: "Très bonne maîtrise du sujet",
                    date: "2024-03-10",
                  },
                ],
              },
            ],
          },
          {
            id: "2",
            nom: "Martin",
            prenom: "Sophie",
            email: "sophie.martin@example.com",
            photo: "/images/paul-diallo.jpg",
            statut: "Inactif" as const,
            derniereConnexion: "2024-03-14T15:45:00",
            progression: [
              {
                id: "1",
                nom: "Module 1",
                progression: 60,
                evaluation: 70,
                dateCompletion: "2024-03-12",
                evaluations: [
                  {
                    note: 70,
                    commentaire: "En bonne progression",
                    date: "2024-03-12",
                  },
                ],
              },
            ],
          },
          {
            id: "3",
            nom: "Bernard",
            prenom: "Pierre",
            email: "pierre.bernard@example.com",
            photo: "/images/marie-kone.jpg",
            statut: "Inactif" as const,
            derniereConnexion: "2024-03-10T09:20:00",
            progression: [
              {
                id: "1",
                nom: "Module 1",
                progression: 45,
                evaluation: 65,
                dateCompletion: "2024-03-08",
                evaluations: [
                  {
                    note: 65,
                    commentaire: "Des efforts supplémentaires sont nécessaires",
                    date: "2024-03-08",
                  },
                ],
              },
            ],
          },
          {
            id: "4",
            nom: "Petit",
            prenom: "Marie",
            email: "marie.petit@example.com",
            photo: "/images/brice-seri.jpg",
            statut: "Actif" as const,
            derniereConnexion: "2024-03-15T11:15:00",
            progression: [
              {
                id: "1",
                nom: "Module 1",
                progression: 90,
                evaluation: 95,
                dateCompletion: "2024-03-14",
                evaluations: [
                  {
                    note: 95,
                    commentaire: "Excellent travail, très bonne maîtrise",
                    date: "2024-03-14",
                  },
                ],
              },
            ],
          },
          {
            id: "5",
            nom: "Robert",
            prenom: "Thomas",
            email: "thomas.robert@example.com",
            photo: "/images/jean-kouassi.jpg",
            statut: "Actif" as const,
            derniereConnexion: "2024-03-13T14:30:00",
            progression: [
              {
                id: "1",
                nom: "Module 1",
                progression: 80,
                evaluation: 88,
                dateCompletion: "2024-03-12",
                evaluations: [
                  {
                    note: 88,
                    commentaire: "Très bon travail, continue ainsi",
                    date: "2024-03-12",
                  },
                ],
              },
            ],
          },
        ];

        const currentCollaborateurIndex = mockCollaborateurs.findIndex(
          (f) => f.id === filleulId
        );
        setCurrentIndex(currentCollaborateurIndex);
        setTotalCollaborateurs(mockCollaborateurs.length);

        const collaborateurData = mockCollaborateurs.find(
          (f) => f.id === filleulId
        );
        if (!collaborateurData) {
          throw new Error("Collaborateur non trouvé");
        }

        setFilleul(collaborateurData);
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "Une erreur est survenue"
        );
      } finally {
        setLoading(false);
      }
    };

    fetchCollaborateurDetails();
  }, [filleulId]);

  const navigateToCollaborateur = (direction: "prev" | "next") => {
    const mockCollaborateurs = [
      {
        id: "1",
        nom: "Dupont",
        prenom: "Jean",
        email: "jean.dupont@example.com",
        photo: "/images/jean-kouassi.jpg",
        statut: "Actif" as const,
        derniereConnexion: "2024-03-15T10:30:00",
        progression: [
          {
            id: "1",
            nom: "Module 1",
            progression: 100,
            evaluation: 85,
            dateCompletion: "2024-03-10",
            evaluations: [
              {
                note: 80,
                commentaire: "Bonne compréhension des concepts de base",
                date: "2024-03-05",
              },
              {
                note: 85,
                commentaire: "Excellente application des connaissances",
                date: "2024-03-07",
              },
              {
                note: 90,
                commentaire: "Très bonne maîtrise du sujet",
                date: "2024-03-10",
              },
            ],
          },
        ],
      },
      {
        id: "2",
        nom: "Martin",
        prenom: "Sophie",
        email: "sophie.martin@example.com",
        photo: "/images/paul-diallo.jpg",
        statut: "Inactif" as const,
        derniereConnexion: "2024-03-14T15:45:00",
        progression: [
          {
            id: "1",
            nom: "Module 1",
            progression: 60,
            evaluation: 70,
            dateCompletion: "2024-03-12",
            evaluations: [
              {
                note: 70,
                commentaire: "En bonne progression",
                date: "2024-03-12",
              },
            ],
          },
        ],
      },
      {
        id: "3",
        nom: "Bernard",
        prenom: "Pierre",
        email: "pierre.bernard@example.com",
        photo: "/images/marie-kone.jpg",
        statut: "Inactif" as const,
        derniereConnexion: "2024-03-10T09:20:00",
        progression: [
          {
            id: "1",
            nom: "Module 1",
            progression: 45,
            evaluation: 65,
            dateCompletion: "2024-03-08",
            evaluations: [
              {
                note: 65,
                commentaire: "Des efforts supplémentaires sont nécessaires",
                date: "2024-03-08",
              },
            ],
          },
        ],
      },
      {
        id: "4",
        nom: "Petit",
        prenom: "Marie",
        email: "marie.petit@example.com",
        photo: "/images/brice-seri.jpg",
        statut: "Actif" as const,
        derniereConnexion: "2024-03-15T11:15:00",
        progression: [
          {
            id: "1",
            nom: "Module 1",
            progression: 90,
            evaluation: 95,
            dateCompletion: "2024-03-14",
            evaluations: [
              {
                note: 95,
                commentaire: "Excellent travail, très bonne maîtrise",
                date: "2024-03-14",
              },
            ],
          },
        ],
      },
      {
        id: "5",
        nom: "Robert",
        prenom: "Thomas",
        email: "thomas.robert@example.com",
        photo: "/images/jean-kouassi.jpg",
        statut: "Actif" as const,
        derniereConnexion: "2024-03-13T14:30:00",
        progression: [
          {
            id: "1",
            nom: "Module 1",
            progression: 80,
            evaluation: 88,
            dateCompletion: "2024-03-12",
            evaluations: [
              {
                note: 88,
                commentaire: "Très bon travail, continue ainsi",
                date: "2024-03-12",
              },
            ],
          },
        ],
      },
    ];

    let newIndex;
    if (direction === "prev") {
      newIndex =
        currentIndex > 0 ? currentIndex - 1 : mockCollaborateurs.length - 1;
    } else {
      newIndex =
        currentIndex < mockCollaborateurs.length - 1 ? currentIndex + 1 : 0;
    }

    const newCollaborateurId = mockCollaborateurs[newIndex].id;
    navigate(`/parrain/filleul/${newCollaborateurId}`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  if (error || !filleul) {
    return (
      <div className="p-8">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          <p>{error || "Collaborateur non trouvé"}</p>
        </div>
      </div>
    );
  }

  const calculerMoyenneProgression = () => {
    if (!filleul.progression.length) return 0;
    const total = filleul.progression.reduce(
      (sum, mod) => sum + mod.progression,
      0
    );
    return Math.round(total / filleul.progression.length);
  };

  const calculerMoyenneEvaluation = () => {
    if (!filleul.progression.length) return 0;
    const total = filleul.progression.reduce(
      (sum, mod) => sum + mod.evaluation,
      0
    );
    return Math.round(total / filleul.progression.length);
  };

  // Fonction pour vérifier si un module est disponible
  const isModuleAvailable = (moduleIndex: number) => {
    if (moduleIndex === 0) return true; // Le premier module est toujours disponible
    const previousModule = filleul.progression[moduleIndex - 1];
    return previousModule && previousModule.progression === 100;
  };

  // Fonction pour obtenir le statut d'un module
  const getModuleStatus = (module: ModuleProgress, index: number) => {
    if (module.progression === 100) return "completed";
    if (!isModuleAvailable(index)) return "locked";
    return "in-progress";
  };

  // Filtrer les modules à afficher (seulement ceux démarrés ou terminés)
  const filteredModules = filleul.progression
    .filter((module, index) => {
      const status = getModuleStatus(module, index);
      return status === "completed" || status === "in-progress";
    })
    .filter((module) => {
      const status = getModuleStatus(
        module,
        filleul.progression.indexOf(module)
      );
      switch (moduleFilter) {
        case "completed":
          return status === "completed";
        case "in-progress":
          return status === "in-progress";
        default:
          return true;
      }
    });

  const NavigationButtons = () => (
    <div className="flex items-center space-x-4">
      <button
        onClick={() => navigateToCollaborateur("prev")}
        className="flex items-center px-4 py-2 bg-white border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500"
      >
        <ChevronLeft className="h-5 w-5 mr-1" />
        Précédent
      </button>
      <span className="text-sm text-gray-500">
        {currentIndex + 1} sur {totalCollaborateurs}
      </span>
      <button
        onClick={() => navigateToCollaborateur("next")}
        className="flex items-center px-4 py-2 bg-white border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500"
      >
        Suivant
        <ChevronRight className="h-5 w-5 ml-1" />
      </button>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* En-tête avec navigation */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-8">
          <div className="mb-4 sm:mb-0">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center text-gray-600 hover:text-gray-900"
            >
              <ArrowLeft className="h-5 w-5 mr-2" />
              Retour
            </button>
          </div>
          <NavigationButtons />
        </div>

        {/* Informations du filleul */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
          <div className="flex items-center">
            <div className="flex-shrink-0 h-16 w-16">
              {filleul.photo ? (
                <img
                  className="h-16 w-16 rounded-full object-cover"
                  src={filleul.photo}
                  alt={`${filleul.prenom} ${filleul.nom}`}
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.src = "/images/avatar-placeholder.png";
                  }}
                />
              ) : (
                <div className="h-16 w-16 rounded-full bg-gray-100 flex items-center justify-center">
                  <span className="text-xl font-medium text-gray-500">
                    {filleul.prenom[0]}
                    {filleul.nom[0]}
                  </span>
                </div>
              )}
            </div>
            <div className="ml-4">
              <h1 className="text-2xl font-bold text-gray-900">
                {filleul.prenom} {filleul.nom}
              </h1>
              <p className="text-gray-500">{filleul.email}</p>
              <div className="flex items-center mt-2">
                <span
                  className={`px-2 py-1 text-xs font-semibold rounded-full ${
                    filleul.statut === "Actif"
                      ? "bg-green-100 text-green-800"
                      : "bg-red-100 text-red-800"
                  }`}
                >
                  {filleul.statut}
                </span>
                <span className="ml-2 text-sm text-gray-500">
                  Dernière connexion :{" "}
                  {new Date(filleul.derniereConnexion).toLocaleDateString()}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Statistiques globales */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-blue-100 text-blue-600">
                <BookOpen className="h-6 w-6" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">
                  Modules complétés
                </p>
                <p className="text-2xl font-bold text-gray-900">
                  {
                    filleul.progression.filter(
                      (module) => module.progression === 100
                    ).length
                  }
                </p>
              </div>
            </div>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-orange-100 text-orange-600">
                <Clock className="h-6 w-6" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">
                  Modules en cours
                </p>
                <p className="text-2xl font-bold text-gray-900">
                  {
                    filleul.progression.filter(
                      (module) =>
                        module.progression > 0 && module.progression < 100
                    ).length
                  }
                </p>
              </div>
            </div>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-green-100 text-green-600">
                <CheckCircle className="h-6 w-6" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">
                  Progression moyenne
                </p>
                <p className="text-2xl font-bold text-gray-900">
                  {calculerMoyenneProgression()}%
                </p>
              </div>
            </div>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-yellow-100 text-yellow-600">
                <Star className="h-6 w-6" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">
                  Note moyenne
                </p>
                <p className="text-2xl font-bold text-gray-900">
                  {calculerMoyenneEvaluation()}/100
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Détails des modules */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold text-gray-900">
                Détails des modules
              </h2>
              <div className="flex items-center space-x-2">
                <Filter className="h-5 w-5 text-gray-400" />
                <select
                  value={moduleFilter}
                  onChange={(e) =>
                    setModuleFilter(e.target.value as ModuleFilter)
                  }
                  className="border border-gray-300 rounded-md text-sm py-1 px-3 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                >
                  <option value="all">Tous les modules</option>
                  <option value="in-progress">En cours</option>
                  <option value="completed">Terminés</option>
                </select>
              </div>
            </div>
            <div className="space-y-4">
              {filteredModules.length === 0 ? (
                <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <p className="text-sm text-gray-600">
                    {moduleFilter === "all"
                      ? "Aucun module n'a encore été démarré."
                      : moduleFilter === "in-progress"
                      ? "Aucun module n'est en cours."
                      : "Aucun module n'est terminé."}
                  </p>
                </div>
              ) : (
                <>
                  {filteredModules.map((module, index) => {
                    const status = getModuleStatus(module, index);
                    return (
                      <div
                        key={module.id}
                        className="border rounded-lg p-4 border-gray-200"
                      >
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center">
                            <h3 className="text-lg font-medium text-gray-900">
                              {module.nom}
                            </h3>
                            {status === "completed" && (
                              <span className="ml-2 px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                                terminé
                              </span>
                            )}
                            {status === "in-progress" && (
                              <span className="ml-2 px-2 py-1 text-xs font-semibold rounded-full bg-orange-100 text-orange-800">
                                En cours
                              </span>
                            )}
                          </div>
                          <div className="flex items-center text-sm text-gray-500">
                            <Clock className="h-4 w-4 mr-1" />
                            {status === "completed"
                              ? `Complété le ${new Date(
                                  module.dateCompletion
                                ).toLocaleDateString()}`
                              : `Dernière connexion le ${new Date(
                                  filleul.derniereConnexion
                                ).toLocaleDateString()}`}
                          </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <p className="text-sm font-medium text-gray-500 mb-1">
                              Progression
                            </p>
                            <div className="w-full bg-gray-200 rounded-full h-2.5">
                              <div
                                className={`h-2.5 rounded-full ${
                                  status === "completed"
                                    ? "bg-green-600"
                                    : "bg-orange-500"
                                }`}
                                style={{ width: `${module.progression}%` }}
                              ></div>
                            </div>
                            <p className="text-sm text-gray-600 mt-1">
                              {`${module.progression}%`}
                            </p>
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-500 mb-1">
                              Moyenne des évaluation
                            </p>
                            <div className="flex items-center">
                              <div className="w-16 h-16 relative">
                                <svg
                                  className="w-full h-full"
                                  viewBox="0 0 36 36"
                                >
                                  <path
                                    d="M18 2.0845
                                      a 15.9155 15.9155 0 0 1 0 31.831
                                      a 15.9155 15.9155 0 0 1 0 -31.831"
                                    fill="none"
                                    stroke="#E5E7EB"
                                    strokeWidth="3"
                                  />
                                  <path
                                    d="M18 2.0845
                                      a 15.9155 15.9155 0 0 1 0 31.831
                                      a 15.9155 15.9155 0 0 1 0 -31.831"
                                    fill="none"
                                    stroke={
                                      status === "locked"
                                        ? "#9CA3AF"
                                        : module.evaluation >= 70
                                        ? "#10B981"
                                        : module.evaluation >= 50
                                        ? "#F59E0B"
                                        : "#EF4444"
                                    }
                                    strokeWidth="3"
                                    strokeDasharray={`${
                                      status === "locked"
                                        ? 0
                                        : module.evaluation
                                    }, 100`}
                                  />
                                </svg>
                                <div className="absolute inset-0 flex items-center justify-center">
                                  <span className="text-sm font-medium">
                                    {status === "locked"
                                      ? "-"
                                      : `${module.evaluation}%`}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                        {status === "locked" && (
                          <p className="mt-2 text-sm text-gray-500">
                            Ce module sera débloqué une fois que le module
                            précédent sera terminé à 100%.
                          </p>
                        )}
                        {status !== "locked" &&
                          module.evaluations &&
                          module.evaluations.length > 0 && (
                            <div className="mt-4 border-t pt-4">
                              <h4 className="text-sm font-medium text-gray-900 mb-3">
                                Détail des évaluations
                              </h4>
                              <div className="space-y-3">
                                {module.evaluations.map((evaluation, index) => (
                                  <div
                                    key={index}
                                    className="bg-gray-50 rounded-lg p-3"
                                  >
                                    <div className="flex items-center justify-between mb-1">
                                      <span className="text-sm font-medium text-gray-900">
                                        Évaluation {index + 1}
                                      </span>
                                      <span
                                        className={`text-sm font-semibold ${
                                          evaluation.note >= 70
                                            ? "text-green-600"
                                            : evaluation.note >= 50
                                            ? "text-yellow-600"
                                            : "text-red-600"
                                        }`}
                                      >
                                        {evaluation.note}%
                                      </span>
                                    </div>
                                    <p className="text-sm text-gray-600 mb-1">
                                      {evaluation.commentaire}
                                    </p>
                                    <p className="text-xs text-gray-500">
                                      {new Date(
                                        evaluation.date
                                      ).toLocaleDateString()}
                                    </p>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                      </div>
                    );
                  })}
                </>
              )}
              {filleul.progression.length > filteredModules.length &&
                moduleFilter === "all" && (
                  <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <p className="text-sm text-gray-600">
                      {filteredModules.length === 0
                        ? "Aucun module n'a encore été démarré. Le premier module est disponible pour commencer."
                        : "Les modules suivants seront débloqués une fois que le module actuel sera terminé."}
                    </p>
                  </div>
                )}
            </div>
          </div>
        </div>

        {/* Navigation en bas de page */}
        <div className="mt-6 pb-8 flex justify-center">
          <NavigationButtons />
        </div>
      </div>
    </div>
  );
};

export default CollaborateurDetail;
